// Ported from nenda-autoplayer's src/components/Playback/Video/VideoPlayer.tsx.
//
// Two branches deleted during the port, per the target architecture (see the
// plan, §5/§6) - both are Foyer-only routing decisions that belong OUTSIDE
// signage-engine/, never inside it:
//
//   1. The Tizen/AvPlayPlayer branch. `WebDeviceController.platform` is always
//      `"web"` (see that file's own doc comment) - Foyer's own provenance-based
//      routing to its existing `AvPlayLayer` happens one layer up, in
//      SignageEngineSurface, not here.
//   2. Redbee DASH resolution (`NendaApiClient.resolveAsset` + PlayResponse
//      parsing). Confirmed unreachable for Foyer: `legacyScheduleFromTimeline`
//      hardcodes `source: "Nenda"` on every asset it emits (checked directly,
//      not assumed) - a Foyer signage MediaAsset is never `MediaSource.Redbee`.
//      Kept as a typed, clearly-unreachable branch rather than silently
//      deleted, matching this port's established pattern elsewhere (see
//      ScheduleClient.getAdSlot's own doc comment for the same call).
//
// Foyer's own PLAYER-6/Verimatrix DRM for protected signage assets
// (src/tv/signage/playbackLicence.ts) and its on-device asset cache
// (src/tv/signage/localAssetCache.ts) are both real, Foyer-only concepts
// with no autoplayer analogue - and both are Foyer modules, so importing
// either directly here would break signage-engine/'s "zero imports from
// Foyer types" boundary (see DrmConfig.ts's own doc comment: Foyer "adapts
// the result into this same shape at the call site"). This component
// accepts an already-resolved `drm?: DrmConfig` prop instead, populated by
// `SignageEngineSurface.tsx` (playerSixDrm.ts's `findSignageItemById` ->
// `playbackLicence.ts` -> `toDrmConfig`) via `DualLayerPlayback.tsx`'s
// `activeDrm` prop for the ACTIVE layer only - a pre-rolling layer gets its
// own licence when it becomes active, same rule SignageSurface.tsx's own
// layer pair follows.
import React, { memo, useEffect, useState } from "react";
import ShakaPlayer from "./ShakaPlayer";
import HtmlVideoPlayer from "./HtmlVideoPlayer";
import { MediaAsset, MediaSource } from "../types/Schedule";
import { DrmConfig } from "../types/DrmConfig";
import { PlaybackErrorLevel } from "../types/error";

interface VideoPlayerProps {
  isSoundEnabled?: boolean;
  mediaAsset: MediaAsset;
  nextSrc?: string;
  startTime?: number;
  /** Already-resolved DRM config for a protected signage asset. See the file
   *  header - populated by SignageEngineSurface.tsx via DualLayerPlayback's
   *  `activeDrm` prop when this instance is the active layer. */
  drm?: DrmConfig;
  /** Threaded straight through to ShakaPlayer/HtmlVideoPlayer - see either
   *  one's own doc comment on the prop. Default `true`. */
  active?: boolean;
  /** Threaded straight through - fires once on the child's first real
   *  `playing` event (see ShakaPlayer.tsx's own doc comment). */
  onPlaybackStarted?: () => void;
  onPlaybackComplete: () => void;
  onError: (error: Error, level: PlaybackErrorLevel) => void;
  onEvent?: (event: string) => void;
}

/**
 * Does this URL look like a DASH/HLS manifest rather than a playable file?
 *
 * A LOCAL copy of `SignageSurface.tsx`'s own `isManifestUrl`, not an import
 * of it - this directory imports nothing from Foyer types or `src/tv/
 * signage/` (see this file's own header, and the port's own boundary rule),
 * so the one-line regex is duplicated rather than shared. Any future
 * autoplayer upstream fix here still diffs back in cleanly; only the test
 * itself needs to match.
 */
function looksLikeManifestUrl(url: string): boolean {
  return /\.(mpd|m3u8)(\?|$)/i.test(url);
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  isSoundEnabled = false,
  mediaAsset,
  drm,
  active = true,
  startTime,
  onPlaybackStarted,
  onPlaybackComplete,
  onError,
  onEvent,
}) => {
  const [resolvedSrc, setResolvedSrc] = useState<string | undefined>(undefined);
  const [playId, setPlayId] = useState<number>(1);

  // Redbee branch: confirmed unreachable for Foyer (see file header). Typed
  // rather than silently dropped so a future Foyer live-TV integration that
  // DOES route through this component (none does today - live TV has its own
  // playbackLicence.ts-driven path) fails loudly instead of playing nothing.
  useEffect(() => {
    if (mediaAsset.source === MediaSource.Redbee) {
      onError(
        new Error(
          "VideoPlayer's Redbee/DASH resolution path is unreachable for Foyer signage - legacyScheduleFromTimeline never emits MediaSource.Redbee"
        ),
        PlaybackErrorLevel.FAILURE
      );
      return;
    }
    setResolvedSrc(encodeURI(mediaAsset.locator));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaAsset.locator, mediaAsset.source, playId]);

  const handlePlaybackComplete = () => {
    setPlayId((prevId) => prevId + 1);
    onPlaybackComplete();
  };

  if (!resolvedSrc) {
    return null;
  }

  /*
   * 2026-09-22/23 incident (hasselbacken/302, live hardware): DRM/Redbee
   * alone is the wrong test for Foyer. Traced end to end -
   * `SignageEngineSurface.tsx` -> `playerSixDrm.ts`'s `toDrmConfig` returns
   * `undefined` for any unprotected item (this catalogue's entire
   * effect-channel content, confirmed unencrypted - `keys=(none)` logged
   * throughout 2026-09-21's own investigation), and `MediaSource.Redbee` is
   * confirmed unreachable for Foyer (see this file's header) - so `isDASH`
   * was FALSE for every single effect-channel item, every one of them a
   * DASH manifest, and every one of them got handed to `HtmlVideoPlayer` (a
   * bare `<video src="...manifest.mpd">`) instead of `ShakaPlayer`. A plain
   * `<video>` element cannot parse a DASH manifest as a source at all - it
   * is not a Foyer- or Tizen-specific limitation, it is what the tag does
   * with an XML document it was told is a video file. Confirmed against the
   * real overnight failure log: every one of 90+ consecutive circuit-breaker
   * trips was a bare, empty native `MediaError` (`Video playback error: : `)
   * - never once a Shaka error, which always carries `code=` in this same
   * file's own error string (`ShakaPlayer.tsx`) and never appeared.
   *
   * `SignageSurface.tsx`'s own `isAbrItem`/`isManifestUrl` already settled
   * this exact question for the legacy surface: "the distinction is the
   * URL, not the item kind" - a channel item and a VOD title both arrive as
   * `kind: "video"` and both may resolve to a manifest, so DRM/provenance
   * cannot be the only signal. `looksLikeManifestUrl` (above) is the same
   * test, duplicated rather than imported per this directory's own zero-
   * Foyer-imports boundary.
   */
  const isDASH =
    mediaAsset.source === MediaSource.Redbee ||
    Boolean(drm) ||
    looksLikeManifestUrl(resolvedSrc);

  return (
    <div style={{ flex: 1, width: "100%", height: "100%" }}>
      {isDASH ? (
        <ShakaPlayer
          key={`${mediaAsset.id}-${playId}`}
          src={resolvedSrc}
          drm={drm}
          active={active}
          isSoundEnabled={isSoundEnabled}
          startTime={startTime}
          onPlaybackStarted={onPlaybackStarted}
          onPlaybackComplete={handlePlaybackComplete}
          onError={onError}
          onEvent={onEvent}
        />
      ) : (
        <HtmlVideoPlayer
          key={`${mediaAsset.id}-${playId}`}
          src={resolvedSrc}
          active={active}
          isSoundEnabled={isSoundEnabled}
          startTime={startTime}
          onPlaybackStarted={onPlaybackStarted}
          onPlaybackComplete={handlePlaybackComplete}
          onError={onError}
          onEvent={onEvent}
        />
      )}
    </div>
  );
};

export default memo(VideoPlayer);
